import React, { useContext, useState } from "react"
import { MusicNoteList, PlusCircle, Share } from "react-bootstrap-icons"
import { CopyToClipboard } from "react-copy-to-clipboard"
import { toast } from "react-toastify"
import { useAppDispatch, useAppSelector } from "../../stores/hooks"
import { closeSongActions } from "../../stores/songActionsSlice"
import { pushToQueue } from "../../stores/mediaPlayerSlice"
import { UserContext } from "../HomeApp"
import { CHER_LINK_IMAGE_URL } from "../../util/constants"
import AddToPlaylistModal from "../AddToPlaylistModal"

const PLAYER_HEIGHT = 100

const rowClass =
  "flex items-center gap-3 px-4 w-full text-left focus:outline-none hover:bg-gray-50 text-sm text-gray-800"
const rowStyle = { height: 25 }

const SongActionsSlideout: React.FC = () => {
  const dispatch = useAppDispatch()
  const { isOpen, song } = useAppSelector((state: any) => state.songActions)
  const { user } = useContext(UserContext)

  const playerQueue = useAppSelector(
    (state: any) => state.mediaPlayer.value.queue,
  )
  const currentMediaItem = useAppSelector(
    (state: any) => state.mediaPlayer.value.currentMediaItem,
  )
  const playerVisible = !!(currentMediaItem || playerQueue?.length)

  const [playlistModalOpen, setPlaylistModalOpen] = useState(false)

  const bottomOffset = playerVisible ? PLAYER_HEIGHT : 0

  const handleClose = () => {
    dispatch(closeSongActions())
  }

  const handleAddToQueue = () => {
    if (!song) return
    dispatch(pushToQueue([song]))
    handleClose()
  }

  const shareMsg = () => (
    <div className="flex">
      <img
        src={CHER_LINK_IMAGE_URL}
        alt="share-icon"
        style={{ width: "50px" }}
        className="mr-2"
      />
      <div>
        <span className="font-bold">Cher Link</span>
        <br />
        copied to your clipboard!
      </div>
    </div>
  )

  if (!isOpen || !song) return null

  return (
    <>
      <div
        className="fixed left-0 right-0 bg-white border-t border-gray-300 shadow-lg"
        style={{ bottom: bottomOffset, zIndex: 30 }}
      >
        {/* Row 1: Song info */}
        <div
          className="flex items-center justify-between px-4 border-b border-gray-200"
          style={{ height: 50 }}
        >
          <div className="flex items-center gap-2 min-w-0">
            {song.cover_image_small && (
              <img
                src={song.cover_image_small}
                alt={song.title}
                style={{ width: 33, height: 33, flexShrink: 0 }}
                className="rounded-sm object-cover"
              />
            )}
            <div className="min-w-0">
              <div className="text-xs font-bold text-gray-900 truncate">
                {song.title}
              </div>
              {(song.band_name || song.album_name) && (
                <div className="text-xs text-gray-500 truncate">
                  {[song.band_name, song.album_name]
                    .filter(Boolean)
                    .join(" - ")}
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none flex-none text-base leading-none ml-2"
          >
            ×
          </button>
        </div>

        {/* Row 2: Add to Playlist */}
        {user && (
          <button
            type="button"
            onClick={() => setPlaylistModalOpen(true)}
            className={rowClass}
            style={rowStyle}
          >
            <MusicNoteList size={14} className="flex-none text-gray-500" />
            Add to Playlist
          </button>
        )}

        {/* Row 3: Add to Queue */}
        <button
          type="button"
          onClick={handleAddToQueue}
          className={rowClass}
          style={rowStyle}
        >
          <PlusCircle size={14} className="flex-none text-gray-500" />
          Add to Queue
        </button>

        {/* Row 4: Share Song */}
        <CopyToClipboard
          text={song.nada_url}
          onCopy={() => {
            toast.success(shareMsg(), {
              position: "top-center",
              autoClose: 2000,
              hideProgressBar: true,
              closeOnClick: true,
              icon: false,
            })
            handleClose()
          }}
        >
          <button type="button" className={rowClass} style={rowStyle}>
            <Share size={14} className="flex-none text-gray-500" />
            Share Song
          </button>
        </CopyToClipboard>
      </div>

      {user && (
        <AddToPlaylistModal
          isOpen={playlistModalOpen}
          onClose={() => {
            setPlaylistModalOpen(false)
            handleClose()
          }}
          songId={song.id}
          songTitle={song.title}
        />
      )}
    </>
  )
}

export default SongActionsSlideout
